import { SceneId } from '@common/api';
import { getChunkCoords, getChunkKey } from '@common/chunkUtils';
import { CHUNK_CONFIG } from '@common/config';

/**
 * Interface for chunk management - allows dependency injection
 * for testing and avoids circular dependencies.
 */
export interface ChunkManager {
  getEntitiesInChunk(sceneId: SceneId, chunkKey: string): Set<number>;
}

/**
 * Player position info for simulation zone calculation.
 */
export interface PlayerSimulationInfo {
  sceneId: SceneId;
  x: number;
  y: number;
  tileSize: number;
  chunkSize: number;
}

/**
 * Service that tracks which entities are within the simulation radius
 * of any player. Entities outside all simulation zones are "dormant" and
 * should not be processed by simulation-bound systems.
 *
 * Uses ChunkManagementService to efficiently query entities by chunk.
 */
export class SimulationService {
  /** Currently active entity IDs */
  private activeEntities: Set<number> = new Set();

  /** Entities that became dormant this tick (were active, now inactive) */
  private newlyDormantEntities: Set<number> = new Set();

  /** Entities that became active this tick (were dormant, now active) */
  private newlyActiveEntities: Set<number> = new Set();

  /** Entities that were active last tick (for transition detection) */
  private previouslyActiveEntities: Set<number> = new Set();

  /** Chunk manager for spatial queries */
  private chunkManager: ChunkManager | null = null;

  /**
   * Set the chunk manager to use for spatial queries.
   * Must be called before updateActiveEntities.
   */
  public setChunkManager(manager: ChunkManager): void {
    this.chunkManager = manager;
  }

  /**
   * Update the set of active entities based on player positions.
   * Should be called once per tick before systems run.
   *
   * @param players - Array of player position info
   */
  public updateActiveEntities(players: PlayerSimulationInfo[]): void {
    if (!this.chunkManager) {
      console.warn('[SimulationService] No chunk manager set');
      return;
    }

    // Save previous state for transition detection
    this.previouslyActiveEntities = new Set(this.activeEntities);

    this.activeEntities.clear();
    this.newlyDormantEntities.clear();
    this.newlyActiveEntities.clear();

    const radius = CHUNK_CONFIG.SIMULATION_RADIUS;

    for (const player of players) {
      const { chunkX, chunkY } = getChunkCoords(
        player.x,
        player.y,
        player.tileSize,
        player.chunkSize,
      );

      // Collect entities from all chunks within simulation radius
      for (let dx = -radius; dx <= radius; dx++) {
        for (let dy = -radius; dy <= radius; dy++) {
          const chunkKey = getChunkKey(chunkX + dx, chunkY + dy);
          const entities = this.chunkManager.getEntitiesInChunk(player.sceneId, chunkKey);
          for (const entityId of entities) {
            this.activeEntities.add(entityId);
          }
        }
      }
    }

    // Find entities that were active but are now dormant
    for (const entityId of this.previouslyActiveEntities) {
      if (!this.activeEntities.has(entityId)) {
        this.newlyDormantEntities.add(entityId);
      }
    }

    // Find entities that were dormant but are now active
    for (const entityId of this.activeEntities) {
      if (!this.previouslyActiveEntities.has(entityId)) {
        this.newlyActiveEntities.add(entityId);
      }
    }
  }

  /**
   * Check if an entity is within any player's simulation radius.
   *
   * @param entityId - Entity to check
   * @returns true if entity should be simulated this tick
   */
  public isActive(entityId: number): boolean {
    return this.activeEntities.has(entityId);
  }

  /**
   * Get all currently active entities.
   */
  public getActiveEntities(): Set<number> {
    return this.activeEntities;
  }

  /**
   * Get entities that became dormant this tick (were active, now inactive).
   * Used by DormancySystem to zero out velocities and create DormantMovement.
   */
  public getNewlyDormantEntities(): Set<number> {
    return this.newlyDormantEntities;
  }

  /**
   * Get entities that became active this tick (were dormant, now active).
   * Used by DormantMovementSystem to clean up dormant movement state.
   */
  public getNewlyActiveEntities(): Set<number> {
    return this.newlyActiveEntities;
  }

  /**
   * Get count of active entities (for debugging/metrics).
   */
  public getActiveCount(): number {
    return this.activeEntities.size;
  }

  /**
   * Reset the service (for testing).
   */
  public reset(): void {
    this.activeEntities.clear();
    this.newlyDormantEntities.clear();
    this.newlyActiveEntities.clear();
    this.previouslyActiveEntities.clear();
    this.chunkManager = null;
  }
}
